import type { NavItem } from "@/types/nav-item.types";

/**
 * Resolves the logo URL for the currently active nav item (or its active sub-item).
 * When `preferMobile` is true, uses `mobileLogoUrl` if the active item provides one.
 * Dedicated mobile logos skip `transform` (they are already theme-ready assets).
 */
export function getActiveNavLogoUrl(
    navItems: NavItem[],
    defaultLogoUrl: string,
    transform?: (url: string) => string,
    preferMobile = false,
): string {
    const apply = (url?: string, skipTransform = false): string => {
        if (!url) return defaultLogoUrl;
        return transform && !skipTransform ? transform(url) : url;
    };

    for (const item of navItems) {
        const activeSubItem = item.subItems?.find((sub) => sub.isActive);

        if (activeSubItem) {
            if (preferMobile) {
                const mobileUrl =
                    activeSubItem.mobileLogoUrl ?? item.mobileLogoUrl;
                if (mobileUrl) return apply(mobileUrl, true);
                return apply(activeSubItem.logoUrl ?? item.logoUrl);
            }
            return apply(activeSubItem.logoUrl ?? item.logoUrl);
        }

        if (item.isActive) {
            if (preferMobile && item.mobileLogoUrl) {
                return apply(item.mobileLogoUrl, true);
            }
            return apply(item.logoUrl);
        }
    }

    return defaultLogoUrl;
}
